import { useEffect, useMemo, useRef } from "react";
import { AudioLines, ShieldCheck, Compass, Languages, Activity, Radio } from "lucide-react";

const CHUNK_SECONDS = 15;

// Unicode-script language guess, mirroring the backend's langid_script module.
function scriptOf(text) {
  for (const ch of String(text || "")) {
    const c = ch.codePointAt(0);
    if (c >= 0x0c00 && c <= 0x0c7f) return "te";
    if (c >= 0x0b80 && c <= 0x0bff) return "ta";
    if (c >= 0x0900 && c <= 0x097f) return "hi";
    if (c >= 0x0c80 && c <= 0x0cff) return "kn";
    if (c >= 0x0d00 && c <= 0x0d7f) return "ml";
    if (c >= 0x0600 && c <= 0x06ff) return "ar";
  }
  return "en";
}

function normalise(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9\u0900-\u0d7f ]/g, "").trim();
}

/**
 * Running transcript-quality meter computed from the live caption stream.
 * Mirrors the four server-side gate components so the number a user watches
 * live is the same shape as the post-meeting Quality Report.
 */
function useLiveQuality(captions, elapsed) {
  return useMemo(() => {
    const list = captions || [];
    const expected = Math.max(1, Math.floor(elapsed / CHUNK_SECONDS));
    const coverage = Math.round(Math.min(100, (list.length / expected) * 100));

    let dupes = 0;
    for (let i = 1; i < list.length; i += 1) {
      const a = normalise(list[i].text);
      const b = normalise(list[i - 1].text);
      if (a && (a === b || (a.length > 12 && (a.includes(b) || b.includes(a))))) dupes += 1;
    }
    const distinct = list.length > 1 ? Math.round(100 - (dupes / (list.length - 1)) * 100) : 100;

    const words = list.reduce((n, c) => n + String(c.text || "").trim().split(/\s+/).filter(Boolean).length, 0);
    const garbled = list.filter((c) => String(c.text || "").includes("\uFFFD")).length;
    const wpc = list.length ? words / list.length : 0;
    const clarity = Math.round(
      Math.max(0, Math.min(100, (wpc / 22) * 100)) * (list.length ? 1 - garbled / list.length : 1)
    );

    const scripts = {};
    list.forEach((c) => {
      const code = c.language || scriptOf(c.text);
      scripts[code] = (scripts[code] || 0) + 1;
    });
    const ranked = Object.entries(scripts).sort((a, b) => b[1] - a[1]);
    const dominant = ranked[0] ? ranked[0][0] : "en";
    const consistency = list.length ? Math.round((scripts[dominant] / list.length) * 100) : 100;

    const score = list.length
      ? Math.round(coverage * 0.35 + distinct * 0.25 + clarity * 0.2 + consistency * 0.2)
      : 0;

    return {
      score,
      components: [
        { key: "coverage", label: "Speech coverage", value: coverage, icon: Activity },
        { key: "distinct", label: "No repetition", value: distinct, icon: ShieldCheck },
        { key: "clarity", label: "Clarity", value: clarity, icon: AudioLines },
        { key: "consistency", label: "Language match", value: consistency, icon: Languages },
      ],
      dominant,
      words,
      segments: list.length,
    };
  }, [captions, elapsed]);
}

function verdict(score) {
  if (score >= 80) return { label: "Excellent", cls: "tone-sage" };
  if (score >= 60) return { label: "Good", cls: "tone-powder" };
  if (score >= 40) return { label: "Marginal", cls: "tone-apricot" };
  return { label: "Poor", cls: "tone-blush" };
}

const LANG_LABEL = { en: "English", hi: "Hindi", ta: "Tamil", te: "Telugu", kn: "Kannada", ml: "Malayalam", ar: "Arabic" };

export const LiveTranscription = ({ captions, agendaAlert, live, elapsed }) => {
  const q = useLiveQuality(captions, elapsed);
  const v = verdict(q.score);
  const feedRef = useRef(null);

  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [captions]);

  return (
    <div data-testid="live-transcription" className="mt-6 grid gap-4 lg:grid-cols-[1fr_300px]">
      {/* ——— caption feed ——— */}
      <div className="yl-surface yl-beam overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {live ? (
              <span className="relative inline-flex w-2.5 h-2.5 shrink-0">
                <span className="absolute inset-0 rounded-full bg-rose-500 yl-pulse-dot" />
                <span className="absolute inset-0 rounded-full bg-rose-500" />
              </span>
            ) : (
              <Radio className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={2} />
            )}
            <span className="eyebrow text-[9px] truncate">
              {live ? "Live transcription" : "Transcription paused"}
            </span>
          </div>
          <span className="font-mono text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider">
            {q.segments} segments · {q.words} words
          </span>
        </div>

        {agendaAlert && agendaAlert.level === "drift" && (
          <div
            data-testid="agenda-alert"
            className="px-5 py-3.5 flex items-start gap-3 border-b border-border tone-apricot"
          >
            <Compass className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <div className="eyebrow text-[9px] text-current">Agenda drift detected</div>
              <p className="text-sm mt-1 leading-snug">{agendaAlert.message}</p>
              {agendaAlert.current_topic && (
                <p className="text-xs mt-1 opacity-80">Current topic: {agendaAlert.current_topic}</p>
              )}
              {agendaAlert.suggestion && <p className="text-xs mt-1.5 italic opacity-80">{agendaAlert.suggestion}</p>}
            </div>
          </div>
        )}
        {agendaAlert && agendaAlert.level === "recovered" && (
          <div data-testid="agenda-recovered" className="px-5 py-3 border-b border-border tone-sage text-sm">
            <strong>Back on agenda.</strong>{" "}
            {agendaAlert.matched_item && `Now discussing: ${agendaAlert.matched_item}`}
          </div>
        )}

        <div ref={feedRef} className="p-5 max-h-72 overflow-y-auto yl-scroll space-y-2.5">
          {(captions || []).length === 0 ? (
            <div className="py-8 text-center">
              <AudioLines className="w-5 h-5 mx-auto text-muted-foreground" strokeWidth={1.6} />
              <p className="text-xs text-muted-foreground mt-3 max-w-sm mx-auto leading-relaxed">
                Listening. Cleaned captions land here every ~{CHUNK_SECONDS} seconds and are broadcast to everyone
                viewing this meeting.
              </p>
            </div>
          ) : (
            (captions || []).map((c, i) => {
              const last = i === (captions || []).length - 1;
              return (
                <div
                  key={c.ts}
                  data-testid={`live-caption-${i}`}
                  className="flex items-start gap-3 rounded-2xl px-3 py-2.5 border yl-fade-in"
                  style={{
                    borderColor: last ? "hsl(var(--cyan) / .4)" : "transparent",
                    background: last ? "hsl(var(--cyan) / .07)" : "transparent",
                  }}
                >
                  <span
                    className="shrink-0 w-7 h-7 rounded-lg grid place-items-center text-[10px] font-bold text-white"
                    style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
                  >
                    {(c.speaker || "S").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    {c.speaker && (
                      <div className="eyebrow text-[9px] text-muted-foreground">{c.speaker}</div>
                    )}
                    <p className={`text-sm leading-snug ${last ? "text-foreground" : "text-foreground/75"}`}>
                      {c.text}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ——— running quality meter ——— */}
      <div data-testid="live-quality-meter" className="yl-surface p-5 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <span className="eyebrow text-[9px]">Transcript quality</span>
          <span className={`${v.cls} rounded-full px-2.5 py-0.5 font-mono text-[9px] font-bold tracking-wider`}>
            {v.label}
          </span>
        </div>

        <div className="mt-4 flex items-end gap-1.5">
          <span data-testid="live-quality-score" className="font-heading text-4xl leading-none">
            {q.score}
          </span>
          <span className="text-xs text-muted-foreground font-semibold pb-1">/100</span>
        </div>
        <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${q.score}%`,
              background: "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--navy)))",
              transition: "width .8s cubic-bezier(.2,.7,.2,1)",
            }}
          />
        </div>

        <div className="mt-6 space-y-3.5">
          {q.components.map((c) => (
            <div key={c.key} data-testid={`live-quality-${c.key}`}>
              <div className="flex items-center justify-between text-[11px] font-semibold">
                <span className="inline-flex items-center gap-1.5 text-foreground/80">
                  <c.icon className="w-3 h-3 text-muted-foreground" strokeWidth={2} />
                  {c.label}
                </span>
                <span className="font-mono text-muted-foreground">{c.value}%</span>
              </div>
              <div className="mt-1.5 h-1 rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${c.value}%`,
                    background: c.value >= 60 ? "hsl(var(--cyan))" : "hsl(var(--ink-warning))",
                    transition: "width .8s cubic-bezier(.2,.7,.2,1)",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <div className="eyebrow text-[9px] text-muted-foreground">Dominant language</div>
          <div className="text-sm font-bold mt-1.5">{LANG_LABEL[q.dominant] || q.dominant}</div>
          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
            A live estimate. The full Quality Guard re-validates every segment after the meeting and may repair or
            exclude some of them.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LiveTranscription;
